import { BaseScheduleShiftViewModel } from '../../../../../entities/BaseScheduleShift/BaseScheduleShift'
import { ShiftConceptViewModelOld } from '../../../../../entities/ShiftConcept/ShiftConcept'
import {
    addDays,
    differenceInDays,
    getHours,
    getMinutes,
    isSameDay,
    setHours,
    setMinutes,
} from '../../../../../helpers/date'
import UpToAndIncludingDate from '../../../../../helpers/date/UpToAndIncludingDate'
import {
    DragState,
    ShiftDraggable,
    ShiftViewModel,
} from '../../../../../models'
import transformShiftToShiftDraggable from '../../../../../services/ShiftConceptService/transformShiftToShiftDraggable'
import doesShiftFallOnWorkDay from '../../../../../services/ShiftService/doesShiftFallOnWorkDay'

type CalendarShift = ShiftViewModel | ShiftConceptViewModelOld | BaseScheduleShiftViewModel

const getShiftTime = (date: Date, shiftDateTime: Date) => {
    const withHours = setHours(date, getHours(shiftDateTime))

    return setMinutes(withHours, getMinutes(shiftDateTime))
}

const placeShiftOnDate = (shift: CalendarShift, targetDate: Date): CalendarShift => {
    const overnightOffset = differenceInDays(shift.end.date, shift.start)

    const start = getShiftTime(targetDate, shift.start)
    const end = new UpToAndIncludingDate(
        getShiftTime(addDays(targetDate, overnightOffset), shift.end.date),
    )

    return {
        ...shift,
        start,
        end,
        shiftPlannings: shift.shiftPlannings.map(shiftPlanning => {
            const planningOvernightOffset = differenceInDays(
                shiftPlanning.end.date,
                shiftPlanning.start,
            )

            return {
                ...shiftPlanning,
                start: getShiftTime(targetDate, shiftPlanning.start),
                end: new UpToAndIncludingDate(
                    getShiftTime(
                        addDays(targetDate, planningOvernightOffset),
                        shiftPlanning.end.date,
                    ),
                ),
            }
        }),
    }
}

const transposeShiftByDays = (shift: CalendarShift, daysToAdd: number): CalendarShift => ({
    ...shift,
    start: addDays(shift.start, daysToAdd),
    end: new UpToAndIncludingDate(addDays(shift.end.date, daysToAdd)),
    shiftPlannings: shift.shiftPlannings.map(shiftPlanning => ({
        ...shiftPlanning,
        start: addDays(shiftPlanning.start, daysToAdd),
        end: new UpToAndIncludingDate(addDays(shiftPlanning.end.date, daysToAdd)),
    })),
})

const getCrossWeekPreviewShifts = (
    shifts: CalendarShift[],
    targetDate: Date,
    selectedDays: Date[],
): ShiftDraggable[] => {
    const targetIndex = selectedDays.findIndex(day => isSameDay(day, targetDate))

    if (targetIndex < 0) {
        return []
    }

    return shifts.map((shift) => {
        const originIndex = selectedDays.findIndex(day => doesShiftFallOnWorkDay(shift, day))
        const previewShift = originIndex < 0
            ? placeShiftOnDate(shift, targetDate)
            : transposeShiftByDays(shift, targetIndex - originIndex)

        return transformShiftToShiftDraggable(previewShift, DragState.preview)
    })
}

export default getCrossWeekPreviewShifts
